<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import ZoneCard from '../Items/ZoneCard.svelte';
	import { format } from 'svelte-i18n';
	import type { Zone } from '../../Types';

	export let zones: Zone[] = [];
	export let mainZoneId: number | null = null;
	export let zonePersonCounts: Map<number | string, number> = new Map();
	export let zoneLimitReached: boolean = false;
	export let hiddenZoneIds: Set<number> = new Set();

	const dispatch = createEventDispatcher();
</script>

<!-- Zones section -->
<div class="section-header">
	<span class="section-header__title">{$format('presence_zones_section', { default: 'Zones' })}</span>
	<button
		class="section-header__add"
		on:click={() => dispatch('createZone')}
		disabled={zoneLimitReached}
		aria-label={$format('presence_zones_section', { default: 'Zones' })}
		title={$format('button_add', { default: 'Add' })}
	>
		<i class="fas fa-plus" aria-hidden="true"></i>
	</button>
</div>

<div class="zones-list">
	{#each zones as zone (zone.id)}
		<ZoneCard
			{zone}
			isMainZone={zone.id === mainZoneId}
			personCount={zonePersonCounts.get(zone.id) ?? 0}
			visible={!hiddenZoneIds.has(zone.id)}
			on:edit
			on:delete
			on:toggleVisible
			on:editSettings
		/>
	{/each}
</div>

<!-- Blind spots section -->
<div class="section-header section-header--mt">
	<span class="section-header__title"
		>{$format('presence_blind_spots_section', { default: 'Blind spots' })}</span
	>
</div>

<div class="zones-list">
	<div class="zone-row">
		<span class="zone-row__icon" style="color:#888;" aria-hidden="true">
			<i class="fas fa-circle-xmark"></i>
		</span>
		<span class="zone-row__name"
			>{$format('presence_blind_spots_section', { default: 'Blind spots' })}</span
		>
		<div class="zone-row__actions">
			<button
				class="zone-row__btn"
				on:click={() => dispatch('editBlindSpots')}
				aria-label={$format('presence_blind_spots_section', { default: 'Blind spots' })}
				title={$format('presence_blind_spots_section', { default: 'Blind spots' })}
			>
				<i class="fas fa-pencil" aria-hidden="true"></i>
			</button>
		</div>
	</div>
</div>

<!-- Boundaries section -->
<div class="section-header section-header--mt">
	<span class="section-header__title"
		>{$format('presence_boundaries_section', { default: 'Boundaries' })}</span
	>
</div>

<div class="zones-list">
	<div class="zone-row">
		<span class="zone-row__icon" style="color:#888;" aria-hidden="true">
			<i class="fas fa-circle-dot"></i>
		</span>
		<span class="zone-row__name"
			>{$format('presence_room_boundary_label', { default: 'Room boundary' })}</span
		>
		<div class="zone-row__actions">
			<button
				class="zone-row__btn"
				on:click={() => dispatch('editBoundary')}
				aria-label={$format('presence_room_boundary_label', { default: 'Room boundary' })}
				title={$format('presence_room_boundary_label', { default: 'Room boundary' })}
			>
				<i class="fas fa-pencil" aria-hidden="true"></i>
			</button>
		</div>
	</div>
</div>

<style>
	/* ── Section headers ─────────────────────────────────────────────────────── */
	.section-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 4px 14px 6px;
	}

	.section-header--mt {
		margin-top: 12px;
	}

	.section-header__title {
		font-size: 14px;
		font-weight: 600;
		color: #a1a2a2;
		letter-spacing: 0.03em;
		text-transform: uppercase;
	}

	.section-header__add {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		border: none;
		background: rgba(255, 255, 255, 0.1);
		color: #eaeaea;
		font-size: 13px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: background-color 0.12s;
	}

	.section-header__add:hover:not(:disabled) {
		background: rgba(255, 255, 255, 0.2);
	}

	.section-header__add:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	/* ── Zone / boundary rows ────────────────────────────────────────────────── */
	.zones-list {
		display: flex;
		flex-direction: column;
		padding: 0 8px;
	}

	.zone-row {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 12px 14px;
		background-color: #1e2023;
		border-radius: 10px;
		margin-bottom: 4px;
	}

	.zone-row__icon {
		font-size: 20px;
		flex-shrink: 0;
		display: flex;
		align-items: center;
		width: 24px;
		justify-content: center;
	}

	.zone-row__name {
		flex: 1;
		font-size: 15px;
		color: #eaeaea;
	}

	.zone-row__actions {
		display: flex;
		gap: 4px;
	}

	.zone-row__btn {
		width: 32px;
		height: 32px;
		border-radius: 8px;
		border: none;
		background: transparent;
		color: #a1a2a2;
		font-size: 14px;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		transition:
			background-color 0.12s,
			color 0.12s;
	}

	.zone-row__btn:hover {
		background-color: rgba(255, 255, 255, 0.1);
		color: #eaeaea;
	}
</style>
